import Handsontable from 'handsontable';
import { getFullNumericValue } from '../../utils/numberNormalization';

type CellRenderer = (
  instance: Handsontable.Core,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties: Handsontable.CellProperties
) => HTMLTableCellElement;

export function createTooltipRenderer(
  baseRenderer?: CellRenderer
): CellRenderer {
  return function tooltipRenderer(
    this: Handsontable.CellProperties,
    instance: Handsontable.Core,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: unknown,
    cellProperties: Handsontable.CellProperties
  ): HTMLTableCellElement {
    if (baseRenderer) {
      baseRenderer.call(this, instance, td, row, col, prop, value, cellProperties);
    } else {
      Handsontable.renderers.TextRenderer.call(
        this,
        instance,
        td,
        row,
        col,
        prop,
        value,
        cellProperties
      );
    }

    const text = getDisplayText(value);
    if (text) {
      td.setAttribute('title', text);
    }

    return td;
  };
}

export function textTooltipRenderer(
  this: Handsontable.CellProperties,
  instance: Handsontable.Core,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties: Handsontable.CellProperties
): HTMLTableCellElement {
  Handsontable.renderers.TextRenderer.call(
    this,
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties
  );

  const text = getDisplayText(value);
  if (text) {
    td.setAttribute('title', text);
  }

  return td;
}

export function numericTooltipRenderer(
  this: Handsontable.CellProperties,
  instance: Handsontable.Core,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties: Handsontable.CellProperties
): HTMLTableCellElement {
  Handsontable.renderers.NumericRenderer.call(
    this,
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties
  );

  const fullValue = getFullNumericValue(value as string | number | null | undefined);
  if (fullValue) {
    td.setAttribute('title', fullValue);
  }

  return td;
}

const RU_DATETIME_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/** ISO-строку → читаемое «ДД.ММ.ГГГГ, ЧЧ:ММ». Пустое/невалидное → ''. */
function formatDateTime(value: unknown): string {
  if (!value) return '';
  const date = new Date(value as string);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('ru-RU', RU_DATETIME_FORMAT);
}

/**
 * Renderer для колонок с датой/временем.
 * Хранимое значение ячейки — ISO-строка (корректно сортируется лексикографически
 * = хронологически), а пользователю показывается читаемый русский формат.
 */
export function dateTimeTooltipRenderer(
  this: Handsontable.CellProperties,
  instance: Handsontable.Core,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties: Handsontable.CellProperties
): HTMLTableCellElement {
  const formatted = formatDateTime(value);
  Handsontable.renderers.TextRenderer.call(
    this,
    instance,
    td,
    row,
    col,
    prop,
    formatted,
    cellProperties
  );

  if (formatted) {
    td.setAttribute('title', formatted);
  }

  return td;
}

function getDisplayText(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  if (typeof value === 'boolean') {
    return value ? 'Да' : 'Нет';
  }

  try {
    return String(value);
  } catch {
    return '';
  }
}

export function addHeaderTooltips(instance: Handsontable.Core): void {
  const rootElement = instance.rootElement;
  if (!rootElement) return;

  const headers = rootElement.querySelectorAll('thead th');
  
  headers.forEach((th) => {
    const headerText = th.textContent?.trim();
    if (headerText) {
      th.setAttribute('title', headerText);
    }
  });
}


